import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Container, Spinner, Alert } from 'react-bootstrap';
import jasLogo from '../../assets/image/jas-logo.png';
import './InvitationAccept.css';

const InvitationAccept = () => {
  const { barcode } = useParams();
  const [visitor, setVisitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [responseType, setResponseType] = useState(null); // 'accepted' or 'rejected'
  const [error, setError] = useState(null);

  const apiBaseUrl = process.env.REACT_APP_API_END_POINT || 'http://localhost:8003/api';

  useEffect(() => {
    const fetchInvitationDetails = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/coming-this-year/${encodeURIComponent(barcode)}`);
        if (response.data.success) {
          setVisitor(response.data.visitor);
          if (response.data.visitor.alreadyAccepted) {
            // If already accepted, store mobile and redirect to registration form immediately
            const mobile = response.data.visitor.mobile || response.data.visitor.mobile_number;
            if (mobile) {
              sessionStorage.setItem('buyerMobile', mobile);
            }
            window.location.href = '/hosted-buyer-registration';
            return;
          }
        } else {
          setError(response.data.message || 'Invitation not found');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Portal closed. Please contact +91 9785610067 for further assistance.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitationDetails();
  }, [barcode, apiBaseUrl]);

  const handleResponse = async (status) => {
    setAccepting(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/coming-this-year/accept/${encodeURIComponent(barcode)}`, { status });
      if (response.data.success) {
        if (status === 'accepted') {
          // Store mobile for auto-login in sessionStorage
          const mobile = response.data.visitor?.mobile_number || visitor?.mobile;
          if (mobile) {
            sessionStorage.setItem('buyerMobile', mobile);
          }
          // Redirect to hosted buyer registration form
          window.location.href = '/hosted-buyer-registration';
          return;
        }
        setConfirmed(true);
        setResponseType(status);
      } else {
        setError('Failed to process request. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error processing response');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="invitation-loader">
        <Spinner animation="border" style={{ color: '#b98b51' }} />
        <p>Loading your invitation for JAS 2026...</p>
      </div>
    );
  }

  if (error) {
    const isDuplicate = error.toLowerCase().includes('already registered');
    const isPortalClosed = error.toLowerCase().includes('portal closed');
    return (
      <Container className="mt-5 text-center">
        <Alert variant={isDuplicate ? "success" : "danger"} style={{ borderLeft: `5px solid ${isDuplicate ? '#198754' : '#2b3035'}` }}>
          {!isDuplicate ? (
            isPortalClosed ? (
              <Alert.Heading style={{ color: '#2b3035' }}>Portal Closed</Alert.Heading>
            ) : (
              <Alert.Heading style={{ color: '#2b3035' }}>Oops! Something went wrong</Alert.Heading>
            )
          ) : (
            <Alert.Heading style={{ color: '#198754' }}>Already Registered</Alert.Heading>
          )}
          <p>{error}</p>
          {!isPortalClosed && (
            <>
              <hr />
              <div className="d-flex justify-content-center">
                <button 
                  onClick={() => window.location.href = isDuplicate ? '/hosted-buyer-registration' : '/'} 
                  className="btn btn-dark" 
                  style={{ backgroundColor: isDuplicate ? '#198754' : '#2b3035', border: 'none' }}
                >
                  {isDuplicate ? 'Go to Registration Form' : 'Go to Homepage'}
                </button>
              </div>
            </>
          )}
        </Alert>
      </Container>
    );
  }

  return (
    <div className="invitation-app-consistent">
      <Container className="d-flex align-items-center justify-content-center min-vh-100">
        <div className="confirmation-card shadow">
          <div className="brand-header">
            <img src={jasLogo} alt="JAS Logo" className="jas-logo-brand" />
          </div>

          <div className="content-body px-4 py-5 text-center">
            {confirmed ? (
              <>
                {responseType === 'accepted' ? (
                  <>
                    <div className="success-check">✓</div>
                    <h1 className="visitor-name">Welcome, {visitor.name}!</h1>
                    <h2 className="confirmation-title">Attendance Confirmed for JAS 2026</h2>
                    <p className="message-text text-muted">
                      Thank you for showing your interest in <strong>JAS 2026</strong>.
                      Your VIP status has been successfully updated.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="success-check" style={{ color: '#6c757d' }}>✕</div>
                    <h1 className="visitor-name">Thank you, {visitor.name}.</h1>
                    <h2 className="confirmation-title">Invitation Declined</h2>
                    <p className="message-text text-muted">
                      We've noted that you won't be able to join us for <strong>JAS 2026</strong>.
                      We hope to see you at our future events!
                    </p>
                  </>
                )}

                <div className="footer-action mt-5">
                  <button className="btn-app-consistent" onClick={() => {
                    if (responseType === 'accepted') {
                      const mobile = visitor?.mobile || visitor?.mobile_number;
                      if (mobile) sessionStorage.setItem('buyerMobile', mobile);
                      window.location.href = '/hosted-buyer-registration';
                    } else {
                      window.location.href = '/';
                    }
                  }}>
                    {responseType === 'accepted' ? 'Go to Registration Form' : 'Back to Portal'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h1 className="visitor-name">Hello, {visitor.name}!</h1>
                <h2 className="confirmation-title">You are invited to JAS 2026</h2>

                <p className="message-text text-muted mb-5">
                  We are excited to invite you to the <strong>JAS 2026</strong>.
                  Would you like to attend the event?
                </p>

                <div className="d-grid gap-3 col-10 mx-auto">
                  <button
                    className="btn-app-consistent accept-btn"
                    onClick={() => handleResponse('accepted')}
                    disabled={accepting}
                  >
                    {accepting && responseType === 'accepted' ? (
                      <Spinner animation="border" size="sm" />
                    ) : 'Yes, I will attend'}
                  </button>

                  <button
                    className="btn btn-outline-secondary py-3 fw-bold"
                    onClick={() => handleResponse('rejected')}
                    disabled={accepting}
                  >
                    {accepting && responseType === 'rejected' ? (
                      <Spinner animation="border" size="sm" />
                    ) : 'No, I cannot attend'}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="card-footer-branding">
            © 2026 JAS
          </div>
        </div>
      </Container>
    </div>
  );
};

export default InvitationAccept;
